import { FC, FormEvent } from 'react';

import { Button, Heading, Notification } from '@purpur/library';

import { useInitializeTranslations } from '@telialt/common-cms/src/useTranslations';
import { EmailInput } from '@telialt/common-email-input/src';
import HtmlContent from '@telialt/common-html-content/src';
import { PhoneInput } from '@telialt/common-phone-input/src';
import { TextArea } from '@telialt/common-text-area/src';
import { TextInput } from '@telialt/common-text-input/src';
import { withParams } from '@telialt/utils/src/helpers';

import { FULL_NAME_MAX_LENGTH, FULL_NAME_VALIDATION_PATTERN } from '../constants';
import { useSubmitFeedback } from '../hooks/useSubmitFeedback';
import { FeedbackSubmitData } from '../model';
import { FEEDBACK_FORM_TEST_IDS } from '../test-ids';
import {
    FeedbackDataForm,
    FeedbackFormContainer,
    FeedbackNotificationContainer,
    FeedbackPrivacyContainer,
    FeedbackSubmitContainer,
} from './FeedbackForm.styles';

export const FeedbackForm: FC = () => {
    const { t } = useInitializeTranslations();

    const { mutate, isSuccess, isError, isPending, reset } = useSubmitFeedback();

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const feedbackData: FeedbackSubmitData = {
            name: formData.get('name') as string,
            email: formData.get('email') as string,
            phone: formData.get('phone') as string,
            message: formData.get('message') as string,
        };

        mutate(feedbackData);
    };

    if (!t) {
        return null;
    }

    if (isSuccess) {
        return (
            <FeedbackFormContainer>
                <Heading tag="h2" variant="title-300">
                    {t['content.feedback_form.title']}
                </Heading>
                <FeedbackNotificationContainer>
                    <Notification heading={t['content.feedback_form.success']} headingTag="h3" status="success" />
                </FeedbackNotificationContainer>
            </FeedbackFormContainer>
        );
    }

    if (isError) {
        return (
            <FeedbackFormContainer>
                <Heading tag="h2" variant="title-300">
                    {t['content.feedback_form.title']}
                </Heading>
                <FeedbackNotificationContainer>
                    <Notification heading={t['content.feedback_form.error.title']} headingTag="h3" status="error">
                        {t['content.feedback_form.error.content']}
                    </Notification>
                </FeedbackNotificationContainer>
                <FeedbackSubmitContainer>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={reset}
                        data-test={FEEDBACK_FORM_TEST_IDS.TRY_AGAIN_BUTTON}
                    >
                        {t['content.feedback_form.error.try_again']}
                    </Button>
                </FeedbackSubmitContainer>
            </FeedbackFormContainer>
        );
    }

    return (
        <FeedbackFormContainer>
            <Heading tag="h2" variant="title-300">
                {t['content.feedback_form.title']}
            </Heading>
            <FeedbackDataForm onSubmit={handleSubmit}>
                <TextInput
                    type="text"
                    dataTest={FEEDBACK_FORM_TEST_IDS.NAME_INPUT}
                    label={t['content.feedback_form.name']}
                    required={true}
                    requiredErrorMessage={t['content.feedback_form.name.required_error_message']}
                    pattern={FULL_NAME_VALIDATION_PATTERN.source}
                    patternErrorMessage={t['content.feedback_form.name.pattern_error_message']}
                    maxLength={FULL_NAME_MAX_LENGTH}
                    maxLengthErrorMessage={withParams(t['content.feedback_form.name.max_length_error_message'], {
                        count: FULL_NAME_MAX_LENGTH,
                    })}
                    name="name"
                />
                <EmailInput
                    dataTest={FEEDBACK_FORM_TEST_IDS.EMAIL_INPUT}
                    label={t['content.feedback_form.email']}
                    required={true}
                    requiredErrorMessage={t['content.feedback_form.email.required_error_message']}
                    name="email"
                />
                <PhoneInput
                    dataTest={FEEDBACK_FORM_TEST_IDS.PHONE_NUMBER_INPUT}
                    label={t['content.feedback_form.phone']}
                    required={true}
                    requiredErrorMessage={t['content.feedback_form.phone.required_error_message']}
                    name="phone"
                />
                <TextArea
                    dataTest={FEEDBACK_FORM_TEST_IDS.MESSAGE_INPUT}
                    label={t['content.feedback_form.message']}
                    required={true}
                    requiredErrorMessage={t['content.feedback_form.message.required_error_message']}
                    defaultValue=""
                    name="message"
                />
                <FeedbackPrivacyContainer>
                    <HtmlContent content={t['content.feedback_form.privacy_policy']} />
                </FeedbackPrivacyContainer>
                <FeedbackSubmitContainer>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isPending}
                        loading={isPending}
                        data-test={FEEDBACK_FORM_TEST_IDS.SUBMIT_BUTTON}
                    >
                        {t['content.feedback_form.submit']}
                    </Button>
                </FeedbackSubmitContainer>
            </FeedbackDataForm>
        </FeedbackFormContainer>
    );
};
